import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import SEO from './SEO';
import './FAQPage.css';
import logo from '../assets/pokeshani-logo.png';

const PrivacyPolicy: React.FC = () => {
  const location = useLocation();
  
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  
  return (
    <>
      <SEO 
        title="Privacy Policy - PokeShani Data Protection & Security"
        description="PokeShani Privacy Policy - Learn how we protect your personal information, ensure data security, and maintain GDPR compliance. Your privacy and security are our top priorities."
        keywords="privacy policy, data protection, GDPR compliance, data security, personal information, privacy rights"
        url="https://pokeshani.com/privacy-policy"
      />
      
      <div className="faq-landing-container">
        <div className="faq-header-section">
          <div className="faq-header-content">
            <Link to="/" className="faq-logo-link">
              <img src={logo} alt="PokeShani Logo" className="faq-logo" />
            </Link>
            <h1 className="faq-main-title">Privacy Policy</h1>
            <p className="faq-subtitle">Last updated: November 11, 2025</p>
          </div>
        </div>
        
        <div className="faq-main-content">
          <div className="important-notice">
            <h2>🔒 Your Privacy Matters</h2>
            <p>At PokeShani, we are committed to protecting your personal information and being transparent about how we collect, use, and protect your data. This policy explains our practices in clear, easy-to-understand language.</p>
          </div>
          <section className="faq-section">
            <h2>Privacy Policy Details</h2>
            
            <div className="faq-item">
              <h3>1. Information We Collect</h3>
              <p>
                PokeShani collects information you provide directly to us, such as when you create an account, make a purchase, or contact us for support. This includes:
              </p>
              <ul>
                <li>Name and email address</li>
                <li>PayPal email for payments</li>
                <li>Digital product information you upload</li>
                <li>Payment and transaction data</li>
                <li>Social media profile links (optional)</li>
              </ul>
            </div>

            <div className="faq-item">
              <h3>2. How We Use Your Information</h3>
              <p>We use the information we collect to:</p>
              <ul>
                <li>Provide, maintain, and improve our services</li>
                <li>Process transactions and send related information</li>
                <li>Send technical notices and support messages</li>
                <li>Respond to your comments and questions</li>
                <li>Monitor and analyze trends and usage</li>
              </ul>
            </div>

            <div className="faq-item">
              <h3>3. Information Sharing</h3>
              <p>
                We do not sell, trade, or otherwise transfer your personal information to third parties except:
              </p>
              <ul>
                <li>With your consent</li>
                <li>To comply with legal obligations</li>
                <li>To protect our rights and safety</li>
                <li>With service providers who assist our operations (like PayPal for payments)</li>
              </ul>
              <p>
                <strong>Please note:</strong> PokeShani is only a platform that connects buyers and sellers of digital products. We do not sell products, handle payments, or act as a financial intermediary. Transaction data between buyers and sellers is processed directly through PayPal.
              </p>
            </div>

            <div className="faq-item">
              <h3>4. Data Security</h3>
              <p>
                We implement appropriate security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the internet is 100% secure.
              </p>
            </div>

            <div className="faq-item">
              <h3>5. Cookies and Analytics</h3>
              <p>
                We use cookies and similar technologies to enhance your experience, analyze usage patterns, and improve our services. We use PostHog for analytics to understand how our platform is used.
              </p>
            </div>

            <div className="faq-item">
              <h3>6. Third-Party Services</h3>
              <p>Our platform integrates with:</p>
              <ul>
                <li>PayPal for payment processing</li>
                <li>Supabase for data storage and authentication</li>
                <li>PostHog for analytics</li>
                <li>Resend for email communications</li>
              </ul>
              <p>
                <strong>Important Notice:</strong> These third-party services have their own privacy policies and terms of service. We are not responsible for the privacy practices of these third parties. We recommend reviewing their privacy policies to understand how they collect and use your information.
              </p>
            </div>

            <div className="faq-item">
              <h3>7. Your Rights</h3>
              <p>You have the right to:</p>
              <ul>
                <li>Access your personal information</li>
                <li>Correct inaccurate information</li>
                <li>Delete your account and data</li>
                <li>Opt out of marketing communications</li>
              </ul>
            </div>

            <div className="faq-item">
              <h3>8. Data Retention</h3>
              <p>
                We retain your personal information for as long as necessary to provide our services and comply with legal obligations. If you delete your account, we will delete your personal information within 30 days, except where we are required to retain certain information by law.
              </p>
            </div>

            <div className="faq-item">
              <h3>9. Legal Basis and Jurisdiction</h3>
              <p>
                This Privacy Policy is governed by the laws of the jurisdiction where PokeShani operates.
              </p>
            </div>

            <div className="faq-item">
              <h3>10. Limitation of Liability</h3>
              <p>
                To the maximum extent permitted by law, PokeShani shall not be liable for any damages, losses, or disputes arising from the use of third-party services or any unauthorized access to your personal information that occurs despite our security measures.
              </p>
            </div>

            <div className="faq-item">
              <h3>11. Changes to This Policy</h3>
              <p>
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "last updated" date.
              </p>
            </div>

            <div className="faq-item">
              <h3>12. Contact Us</h3>
              <p>
                If you have any questions about this Privacy Policy, please contact us at support@pokeshani.com.
              </p>
            </div>
          </section>
        </div>
        
        <div className="faq-footer">
          <div className="faq-footer-content">
            <p>Questions about our privacy practices? <a href="https://x.com/PokeShani" target="_blank" rel="noopener noreferrer" className="contact-link">Contact our support team</a></p>
            <Link to="/" className="back-home-btn">← Back to Home</Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default PrivacyPolicy;